/* eslint-disable lit-a11y/click-events-have-key-events */
import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import {
  querySelectorAllDeep,
  querySelectorDeep,
} from 'query-selector-shadow-dom';
import {
  getClickedLinkElement,
  pushAnalyticsPackageInteraction,
  isInViewport,
  dominantColorGenerator,
  AppClassToggle,
  readmeCleanser,
} from '../../functions';
import { StateController } from '../../state';
import type { OpenModalEvent } from '../../events';
import type { Package } from './entities';

const KEYCODE_ESCAPE = 'Escape';
const KEYCODE_TAB = '9';

@customElement('umb-market-package-modal')
export class UmbMarketPackageModal extends LitElement {
  @property({ type: Boolean, reflect: true })
  visible = false;

  @state()
  private _package!: Package;

  #hasResized = false;

  /**
   * Set only when opening the overlay, to the
   * value of _lastFocusedElement
   */
  #refocusElement?: HTMLElement;

  get #showFooter() {
    return (
      this._package?.relatedPackagesSelected?.length ||
      this._package?.relatedPackagesBySameAuthor.length ||
      this._package?.relatedPackagesRequiredForUmbracoCloud.length ||
      this._package?.subPackages.length
    );
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.#handleFocus);
    window.addEventListener('popstate', this.close);
    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#handleFocus);
    window.removeEventListener('popstate', this.close);
    this.removeEventListener(
      'install-button-click',
      this.#handleInstallBtnClick
    );
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('resize', this.#handleResize);
  }

  protected createRenderRoot() {
    const root = super.createRenderRoot();
    this.addEventListener('install-button-click', this.#handleInstallBtnClick);
    this.addEventListener('click', this.#handleClick);

    return root;
  }

  #handleResize = () => {
    this.#hasResized = true;
  };

  #handleClick = async (e: Event) => {
    const element = getClickedLinkElement(e);
    if (!element) return;

    // package content can link to another package, which requires spawning a new modal
    if (
      element instanceof HTMLAnchorElement &&
      !element.classList.contains('card')
    ) {
      const { pathname } = new URL(element.href);
      if (pathname.startsWith('/package/')) {
        e.preventDefault();

        const packageId = pathname.split('/package/').pop()!;
        this.dispatchEvent(
          new CustomEvent<OpenModalEvent>('open-modal', {
            bubbles: true,
            composed: true,
            detail: {
              packageId,
              direct: false,
            },
          })
        );
      }
    }

    window.dispatchEvent(
      new CustomEvent('link-click', {
        detail: {
          packageData: this._package,
          element,
        },
      })
    );
  };

  #handleInstallBtnClick = (e: Event) => {
    const { command } = (e as CustomEvent).detail;
    pushAnalyticsPackageInteraction(this._package!, 'install', {
      action: command,
    });
  };

  #getAllModals = () => Array.from(querySelectorAllDeep(this.tagName));

  #handleFocus = (e: KeyboardEvent) => {
    if (e.key === KEYCODE_ESCAPE) {
      // only close the last modal added to the dom
      const lastModalId = this.#getAllModals()?.pop()?.id;
      // and check for expanded carousel
      const carousel = querySelectorDeep('umb-market-carousel.fullscreen');
      if (lastModalId === this.id && !carousel) {
        this.close();
      }

      return;
    }

    const isTabPressed = e?.key === 'Tab' || e?.key === KEYCODE_TAB;

    if (!isTabPressed) {
      return;
    }

    const focusableEls = querySelectorAllDeep(
      'umb-market-package-modal button, umb-market-package-modal a[href], umb-market-carousel'
    );

    const firstFocusableEl = focusableEls[0] as HTMLElement;
    const lastFocusableEl = focusableEls.at(-1) as HTMLElement;

    if (e.shiftKey) {
      if (StateController.lastFocusedElement === firstFocusableEl) {
        lastFocusableEl?.focus();
        e.preventDefault();
      }
    } else if (StateController.lastFocusedElement === lastFocusableEl) {
      firstFocusableEl?.focus();
      e.preventDefault();
    }
  };

  closeModal() {
    this.close();
  }

  close = (popState?: PopStateEvent) => {
    if (popState?.state === 'vaadin-router-ignore') {
      return;
    }

    // this is fun - if the window has resized while the modal was open, and that has resulted
    // in the triggering card now being outside the viewport, we can't return focus to that
    // element, else we end up scrolling the page absurdly. Instead, if the page has resized,
    // return focus to the nearest card-deck element.
    if (this.#hasResized && this.#refocusElement) {
      if (isInViewport(this.#refocusElement)) {
        this.#refocusElement.focus();
      } else if (this.#refocusElement.offsetParent) {
        (this.#refocusElement.offsetParent as HTMLElement).focus();
      }
    }

    window.removeEventListener('popstate', this.close);
    document.removeEventListener('keydown', this.#handleFocus);

    this.dispatchEvent(
      new CustomEvent('close-modal', {
        bubbles: true,
        composed: true,
        detail: {
          packageId: this._package?.packageId,
        },
      })
    );

    // Niels note: Worth investigation if this overlay-in can use the visible attribute as well?
    // Nathan note: Would really like to refactor this to use native modals. One for future.
    this.classList.remove('overlay-in');
    this.visible = false;

    setTimeout(() => {
      this.remove();

      if (this.#getAllModals()?.length === 0) {
        AppClassToggle('overlay-in', 'remove');
      }
    }, 500);
  };

  open = async (packageData: Package) => {
    this.#refocusElement = StateController.lastFocusedElement;

    readmeCleanser(packageData);
    dominantColorGenerator(this, packageData.iconDominantColor);

    this._package = packageData;

    document.addEventListener('keydown', this.#handleFocus);

    setTimeout(() => {
      this.visible = true;
    }, 250);

    // delay focus within until overlay is positioned, to avoid
    // outline / focus-state displaying as overlay slides up
    setTimeout(() => {
      const section = this.renderRoot.querySelector('section')!;
      section.tabIndex = 0;
      section.focus();
    }, 750);
  };

  render() {
    return html` <section
      aria-hidden="true"
      role="dialog"
      .aria-labelledby=${this._package?.title}
    >
      ${when(
        this._package,
        () => html` <umb-market-page-title
          .pageTitle=${this._package?.title}
          pageDescription=""
          .srOnly=${true}
        ></umb-market-page-title>`
      )}
      <header @click=${this.closeModal}>
        <umb-market-button type="button" class="modal-close-btn">Close</umb-market-button>
      </header>
      <div class="overlay">
        ${when(
          this._package,
          () =>
            html`<umb-market-package
              .package=${this._package}
            ></umb-market-package>`
        )}
        ${when(
          this.#showFooter,
          () => html`<umb-market-package-footer
            .package=${this._package}
          ></umb-market-package-footer>`
        )}
      </div>
    </section>`;
  }

  static styles = [
    css`
      :host {
        display: block;
        z-index: 6050;
        position: fixed;
        top: 100%;
        bottom: 0;
        left: 0;
        width: 100%;
        transition: top cubic-bezier(1, 0, 0, 0.5) 480ms;
        text-align: left;

        --header-height: 120px;
      }

      :host([visible]) {
        top: 0;
        transition: top cubic-bezier(0.5, 0, 0, 1) 480ms;
      }

      :host:after {
        content: '';
        display: block;
        position: fixed;
        top: 0;
        bottom: 0;
        width: 100%;
        background: var(--dominant-color-header, transparent);
        opacity: 0;
        transition: opacity ease-in-out 750ms 100ms,
          background ease-in-out 500ms;
        will-change: opacity;
        z-index: -1;
      }

      :host(.overlay-in):after {
        opacity: 0.75;
      }

      .overlay {
        background: var(--color-body-surface);
      }

      header {
        box-sizing: border-box;
        display: flex;
        justify-content: end;
        align-items: flex-start;
        padding: 0;
        height: var(--header-height);
        max-width: var(--max-width);
        margin:0 auto;
      }

      umb-market-button {
        --button-border-radius: 100px;
        --button-padding: var(--uui-size-4) var(--uui-size-8);
        --button-width: auto;
        align-self: start;
        margin: var(--s-spacer) var(--s-spacer) 0 0;
      }

      @media (min-width: 767px) {
        umb-market-button {
          margin-right: var(--l-spacer);
        }
      }

      @media (min-width: 1200px) {
        umb-market-button {
          align-self: center;
          margin-top: 0;
        }
      }

      @media (min-width: 1774px) {
        umb-market-button {
          margin-right: 0;
        }
      }

      section {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 100%;
        overflow: hidden scroll;
      }
    `,
  ];
}
